import React, { useCallback } from 'react';
import {
  regHook,
  getHooks,
  getSyncHook,
  QueueType,
  unRegHook,
  HookType,
} from '../js-api-tools';
import ErrorBoundary from '../errorBoundary';

export type IReactComponent<P = any> =
  | React.ClassicComponentClass<P>
  | React.ComponentClass<P>
  | React.FunctionComponent<P>
  | React.ForwardRefExoticComponent<P>

export interface Fn {
  order?: number;
  ovProps?: (props: object) => object;
  ovComponent?: (Com: IReactComponent<any>) => IReactComponent<any>;
}

export interface OverwriteType { [key: string]: QueueType };

const ovComponentCache: { [key: string]: [IReactComponent<any> | null, HookType[]] } = {};

function areHooksEqual(
  nextHooks: Array<any>,
  prevHooks: Array<any> | null,
) {

  if (prevHooks === null) {
    return false;
  }

  if (prevHooks.length !== nextHooks.length) {
    return false;
  }

  for (let i = 0; i < prevHooks.length && i < nextHooks.length; i++) {
    if (nextHooks[i] === prevHooks[i]) {
      continue;
    }
    return false;
  }
  return true;
}

function getOvComponentCache (app: string, name: string, creator: () => any) {
  const key = `middleware.${app}.${name}.ovComponent`;
  let nextHooks = getHooks(`middleware.${app}`, `${name}.ovComponent`) || [];
  nextHooks = Array.isArray(nextHooks) ? [...nextHooks] : [];
  const prevState = ovComponentCache[key];
  if (prevState !== null && prevState !== undefined) {
    if (nextHooks !== null) {
      const prevHooks: Array<any> | null = prevState[1];
      if (areHooksEqual(nextHooks, prevHooks)) {
        return prevState[0];
      }
    }
  }
  const nextValue = creator();
  ovComponentCache[key] = [nextValue, nextHooks];
  return nextValue;
}

function wrap(app: string, name: string, Com: IReactComponent<any>): IReactComponent<any> {
  const appName = `middleware.${app}`;
  let Wrap: IReactComponent<any> = React.forwardRef((props, ref) => {

    let NewCom = Com;
    let newProps = props;

    const ovProps = getSyncHook(appName, `${name}.ovProps`);
    
    // 处理props属性复写
    if (typeof ovProps === 'function') {
      newProps = ovProps(newProps) || newProps;
    }

    // 从缓存中获取复写的组件
    NewCom = getOvComponentCache(
      app, name,
      () => {
        const ovComponent = getSyncHook(appName, `${name}.ovComponent`);
        // 处理class组件复写
        if (typeof ovComponent === 'function') {
          NewCom = ovComponent(NewCom) || NewCom;
          return NewCom;
        }
        return null;
      },
    ) || NewCom;

    const handleError = useCallback((error, errorInfo) => {
      const errMsg = `[${app}.${name}]:${error}`;
      console?.error?.(errMsg, errorInfo);
    }, []);

    return (
      <ErrorBoundary onError={handleError} weId={`${newProps.weId || ''}_6ca29d`}>
        <NewCom ref={ref} {...newProps} />
      </ErrorBoundary>
    );
  });
  Wrap.displayName = `${app}.${name}`;
  return Wrap;
}

export function middleware(
  app: string,
  name: string
): <T extends IReactComponent<any>>(
  target: T
) => T

export function middleware(
  app: string,
  name: string
) {
  return (Com: IReactComponent<any>) => wrap(app, name, Com)
}

export function getOverwrite(app?: string, name?: string) {
  const hooks = getHooks() as QueueType; // 获取所有注册的钩子

  if (app && hooks[`middleware.${app}`]) {
    let appHooks = hooks[`middleware.${app}`];
    if (name) {
      const ovPropsHooks = appHooks[`${name}.ovProps`];
      const ovComponentHooks = appHooks[`${name}.ovComponent`];
      if (ovPropsHooks || ovComponentHooks) {
        return { [app]: {
          [name]: {
            ovProps: ovPropsHooks || [],
            ovComponent: ovComponentHooks || [],
          },
        } };
      }
    }
    return appHooks;
  }

  return Object.keys(hooks).reduce((targetOverwrites, key) => {
    if (/^middleware\./.test(key) && hooks[key]) {
      const appHooks = hooks[key];
      const app = key.replace(/^middleware\./, '');
      targetOverwrites[app] = Object.keys(appHooks).reduce((targetHooks, key) => {
        if (/\.ovProps$/.test(key)) {
          const name = key.replace(/\.ovProps$/, '');
          targetHooks[name] = targetHooks[name] || {};
          targetHooks[name]['ovProps'] = appHooks[key] || [];
        }
        if (/\.ovComponent$/.test(key)) {
          const name = key.replace(/\.ovComponent$/, '');
          targetHooks[name] = targetHooks[name] || {};
          targetHooks[name]['ovComponent'] = appHooks[key] || [];
        }
        return targetHooks;
      }, {} as QueueType);
    }
    return targetOverwrites;
  }, {} as OverwriteType);
}

export function regOvProps(
  app: string,
  name: string,
  fn: (props: object) => object,
  order: number = 0
) {
  app = `middleware.${app}`;
  const comName = `${name}.ovProps`;

  regHook(app, comName, fn, order);

  return getSyncHook(app, comName);
}

export function regOvComponent(
  app: string,
  name: string,
  fn: (Com: IReactComponent<any>) => IReactComponent<any>,
  order: number = 0
) {
  app = `middleware.${app}`;
  const comName = `${name}.ovComponent`;

  regHook(app, comName, fn, order);

  return getSyncHook(app, comName);
}

// 取消注册props复写钩子
export function unRegOvProps(
  app: string,
  name: string,
  fn: (props: object) => object,
) {
  app = `middleware.${app}`;
  const comName = `${name}.ovProps`;

  return unRegHook(app, comName, fn);
}

// 取消注册component复写钩子
export function unRegOvComponent(
  app: string,
  name: string,
  fn: (Com: IReactComponent<any>) => IReactComponent<any>,
) {
  app = `middleware.${app}`;
  const comName = `${name}.ovComponent`;

  return unRegHook(app, comName, fn);
}